// Active-event store — mirrors apps/ios/Shared/Services/ActiveEventStore.swift
// for the browser. URL is the source of truth for the *current* event
// (route params `orgSlug` + `eventSlug`); the store holds the joined-events
// list and resolves slug pairs → numeric ids.
//
// activate() runs on every event-route mount: it calls BA's
// /organization/set-active so the session row's activeOrganizationId
// stays in lockstep with the URL. Tenant scoping is by org now (BA teams
// retired in favor of QB-native event_roles).

import { useEffect } from "react"
import { useParams } from "@tanstack/react-router"
import { create } from "zustand"
import { api, ApiError } from "./api"
import { invalidateJwt } from "./auth"
import { backendUrl } from "./backend-url"
import { apiFetch } from "./d1-bookmark"
import { useUserOrgs } from "./orgs"
import type { MyEventRow } from "./types"

type State = {
  events: MyEventRow[]
  loading: boolean
  loaded: boolean
  error: string | null
  /// Set after `activate()` succeeds — the eventId currently mirrored into
  /// the BA session. Used to skip redundant set-active calls when the user
  /// navigates between sub-routes of the same event.
  activeEventId: string | null
  /// The orgId currently pinned in the BA session. Tracked separately from
  /// activeEventId so org-scoped routes (/<org>/settings/...) can dedup
  /// their set-active calls without going through the event-activation path.
  activeOrgId: string | null
  load: () => Promise<void>
  bySlug: (orgSlug: string, eventSlug: string) => MyEventRow | undefined
  byId: (id: string) => MyEventRow | undefined
  /// Lookup helper for store consumers OUTSIDE React render. React
  /// callers should select the orgId primitive directly to keep
  /// reference equality stable (see useActiveOrg).
  orgIdBySlug: (orgSlug: string) => string | undefined
  /// Sync BA's session with the given event. No-op if already active.
  activate: (eventId: string) => Promise<void>
  /// Pin the BA session to an org without resolving an event — used by the
  /// /<org>/ portal and /<org>/settings/* routes. No-op if already active.
  activateOrg: (orgId: string) => Promise<void>
  /// Clear BA's active org so the session is org-less. Called by the root
  /// `/` (org picker) — that page must have NO active org; only a URL with
  /// an `:orgSlug` (via activate/activateOrg) sets one. No-op if already clear.
  clearOrg: () => Promise<void>
}

// Better Auth endpoints exposed by the `organization` plugin. Hit via the
// fetch wrapper directly (not `api()`) because they live under `/auth/v1/*`
// instead of `/api/v1/*` and rely on the BA session cookie.
async function baFetch(path: string, body: unknown) {
  const res = await apiFetch(backendUrl(`/auth/v1${path}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => "")
    const err = new Error(`${path} failed: ${res.status} ${text}`) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  return res.json().catch(() => ({}))
}

/// Orgs whose `set-active` was refused — the signed-in user holds no Better
/// Auth seat there. EXTERNAL CREW are exactly this: an `event_roles` grant on
/// one event of an org they don't belong to. See the native twin,
/// `orgDenied` in the AAA apps' AuthStore — same contract, same reason.
const orgDenied = new Set<string>()

// Coalesce concurrent activate(eventId) calls. Without this, every
// useActiveEvent() effect + every api() call's ensureActiveOrgForCurrentRoute
// races the same set-active before the first one's `set({activeEventId})`
// lands, and we get a /auth/v1/organization/set-active storm.
const activateInFlight = new Map<string, Promise<void>>()
// Same coalescing for org-only activation paths.
const activateOrgInFlight = new Map<string, Promise<void>>()

async function ensureUserOrgsLoaded(): Promise<void> {
  let state = useUserOrgs.getState()
  if (state.loaded) return
  if (!state.loading) void state.load()
  await new Promise<void>((resolve) => {
    if (useUserOrgs.getState().loaded) return resolve()
    const unsub = useUserOrgs.subscribe((next) => {
      if (next.loaded) {
        unsub()
        resolve()
      }
    })
  })
  state = useUserOrgs.getState()
  if (!state.loaded) throw new Error("Failed to load organizations")
}

/// Awaits the admin-events list (useEvents) being loaded — kicks the load if
/// it hasn't started. Non-hook; safe to call from a router loader. The events
/// list is what bySlug() resolves against, so callers that need a slug→eventId
/// resolution must await this first.
async function ensureEventsLoaded(): Promise<void> {
  let state = useEvents.getState()
  if (state.loaded) return
  if (!state.loading) void state.load()
  await new Promise<void>((resolve) => {
    if (useEvents.getState().loaded) return resolve()
    const unsub = useEvents.subscribe((s) => {
      if (s.loaded) {
        unsub()
        resolve()
      }
    })
  })
}

function orgIdBySlugFromStores(orgSlug: string): string | undefined {
  return (
    useUserOrgs.getState().orgs.find((org) => org.slug === orgSlug)?.id ??
    useEvents.getState().orgIdBySlug(orgSlug)
  )
}

export const useEvents = create<State>((set, get) => ({
  events: [],
  loading: false,
  loaded: false,
  error: null,
  activeEventId: null,
  activeOrgId: null,
  async load() {
    // Never populate this store on the server. It's a MODULE SINGLETON shared by
    // every request on the SSR isolate, so a server-side load would put one
    // user's events where the next request can read them. Today that's masked
    // only by accident — api() has no cookie to forward server-side (see
    // core/api.ts), so the fetch always fails — and the day someone adds
    // cookie forwarding it becomes a cross-request leak. Settle as loaded+empty
    // so `ensureEventsLoaded()` never hangs an SSR loader; the server renders
    // the loading gate ($eventSlug/route.tsx) and the client loads the real
    // list after hydration.
    if (import.meta.env.SSR) {
      set({ events: [], loading: false, loaded: true, error: null })
      return
    }
    set({ loading: true, error: null })
    try {
      // Admin workspace = events the user can ADMINISTER (org member or
      // organizer), not just ones they're a guest of. See /me/admin-events
      // (the org-admin counterpart to the attendee /me/events).
      //
      // One transient retry: this list is what resolves every URL slug → eventId,
      // and a failure is terminal for the whole event workspace (it sets
      // loaded:true, so nothing re-runs it on its own). A cold worker or dropped
      // connection on first open shouldn't cost the user a manual retry. Auth
      // failures are not transient — surface those immediately.
      const fetchEvents = () => api<{ events: MyEventRow[] }>("/api/v1/me/admin-events")
      const res = await fetchEvents().catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) throw err
        return fetchEvents()
      })
      set({
        events: res.events ?? [],
        loading: false,
        loaded: true,
        error: null,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load events"
      set({ loading: false, loaded: true, error: msg })
    }
  },
  bySlug(orgSlug, eventSlug) {
    return get().events.find(
      (e) => e.organizationSlug === orgSlug && e.slug === eventSlug,
    )
  },
  byId(id) {
    return get().events.find((e) => e.eventId === id)
  },
  orgIdBySlug(orgSlug) {
    return get().events.find((e) => e.organizationSlug === orgSlug)?.organizationId ?? undefined
  },
  async activate(eventId) {
    // Skip when the BA session is already pinned to this event's org —
    // BA `set-active` is idempotent but the extra round-trip is
    // wasteful on sub-route navigation.
    if (get().activeEventId === eventId) return
    const existing = activateInFlight.get(eventId)
    if (existing) return existing
    const p = (async () => {
      const row = get().byId(eventId)
      if (!row?.organizationId) {
        throw new Error(`No event ${eventId} in joined-events list`)
      }
      // EXTERNAL CREW: the event is in their workspace (via `event_roles`)
      // but they hold no seat in its org, so BA refuses set-active. That threw
      // here, and api.ts awaits this before EVERY /api/v1/* call — so the
      // whole console workspace was dead for the people the crew feature
      // exists for.
      //
      // Fall back to running ORG-LESS: explicitly unset the session's active
      // org, so the next JWT carries no org claim and nothing in this tab can
      // mistake a leftover claim for some OTHER org as membership here. The
      // server no longer needs this — since compiler 0.66.2 the
      // /event/:eventId namespace tenant-binds its `{ roles }` lane and
      // re-evaluates a foreign-org claim through `organizerOf` itself (see
      // quickback.config.ts under authz.namespaces.eventScope) — but it keeps
      // the UI state honest. Same contract as the native apps' plain-token
      // fallback.
      const denied = orgDenied.has(row.organizationId)
      if (!denied) {
        try {
          await baFetch("/organization/set-active", {
            organizationId: row.organizationId,
          })
        } catch (e) {
          const status = (e as { status?: number })?.status
          // Only a membership refusal (403) falls back. A 401 is an expired
          // session, not "no seat" — treating it as denial would pin this org
          // as denied for the tab even after re-auth. Network/5xx must surface.
          if (status !== 403) throw e
          orgDenied.add(row.organizationId)
        }
      }
      if (orgDenied.has(row.organizationId)) {
        await baFetch("/organization/set-active", { organizationId: null }).catch(() => {})
      }
      // Bust the JWT cache so the next mint picks up the freshly-set
      // activeOrganizationId. Without this, queued /api/v1/* calls in
      // this tab present a stale JWT and 403 on org-firewalled actions.
      invalidateJwt()
      set({
        activeEventId: eventId,
        // Null for external crew: nothing in this tab may assume org
        // membership just because an event of that org is open.
        activeOrgId: orgDenied.has(row.organizationId) ? null : row.organizationId,
      })
    })()
    activateInFlight.set(eventId, p)
    try {
      await p
    } finally {
      activateInFlight.delete(eventId)
    }
  },
  async activateOrg(orgId) {
    // Dedup: if the BA session is already pinned to this org, skip the
    // network call entirely. Org-scoped settings pages call this on
    // mount; without the guard, every sub-route navigation re-fires
    // set-active for an org that's already active.
    if (get().activeOrgId === orgId) return
    // EXTERNAL CREW landing on an org-only route (/<org>/, /<org>/settings/*):
    // they hold no seat, so set-active can only 403. Stay org-less rather than
    // throwing — api.ts awaits this before every call, and a throw here made
    // the whole /<org>/ surface dead for them. Same contract as activate().
    if (orgDenied.has(orgId)) return
    const existing = activateOrgInFlight.get(orgId)
    if (existing) return existing
    const p = (async () => {
      try {
        await baFetch("/organization/set-active", { organizationId: orgId })
      } catch (e) {
        if ((e as { status?: number })?.status !== 403) throw e
        orgDenied.add(orgId)
        return
      }
      // Bust JWT so the next mint picks up the new activeOrganizationId.
      invalidateJwt()
      set({
        activeOrgId: orgId,
        // Org changed → previous event activation is no longer valid;
        // clear so a later activate(eventId) re-runs instead of short-
        // circuiting on the stale event id.
        activeEventId: null,
      })
    })()
    activateOrgInFlight.set(orgId, p)
    try {
      await p
    } finally {
      activateOrgInFlight.delete(orgId)
    }
  },
  async clearOrg() {
    // Already org-less → nothing to do (and avoids re-firing on every
    // root re-render). Clear local state first so a concurrent re-fire
    // short-circuits, then unset BA's session active org + bust the JWT
    // so the next mint carries no orgId.
    if (get().activeOrgId === null) return
    set({ activeOrgId: null, activeEventId: null })
    invalidateJwt()
    try {
      await baFetch("/organization/set-active", { organizationId: null })
    } catch {
      // Best-effort — local state is already cleared; the server session
      // reconciles on the next set-active or sign-in.
    }
  },
}))

export function resetEventsStore() {
  // Denials belong to the identity that earned them — the next sign-in on
  // this tab may well hold a seat in the org the last user didn't.
  orgDenied.clear()
  useEvents.setState({
    events: [],
    loading: false,
    loaded: false,
    error: null,
    activeEventId: null,
    activeOrgId: null,
  })
}

/// Awaited by api.ts before every /api/v1/* mint so a page that mounts
/// queries on the FIRST render doesn't fire them with a no-org JWT
/// while useActiveEvent's effect is still in flight. Idempotent — does
/// nothing when the URL isn't event/org-scoped, when the events list hasn't
/// loaded yet (no slug→id resolution possible), or when the target is
/// already activated.
///
/// Handles three URL shapes (all set BA's active ORG — teams are retired):
///   /<org>/<event>/...          → activate(eventId) [resolves org from event]
///   /<org>/settings/...         → activateOrg(orgId)
///   /<org>/                     → activateOrg(orgId) [event picker etc.]
///
/// "settings" + "clients" are reserved at segment[1] (i.e. /<org>/settings/*,
/// /<org>/clients/*) so an event can't take those slugs. This mirrors the
/// worker's reserved-slug list.
let inFlightActivate: Promise<void> | null = null
export async function ensureActiveOrgForCurrentRoute(): Promise<void> {
  if (typeof window === "undefined") return
  const segments = window.location.pathname.split("/").filter(Boolean)
  if (segments.length < 1) return
  // Reserved prefixes that aren't an org slug.
  const reserved = new Set(["account", "api", "auth", "broadcast", "realtime", "invite", "health", "openapi.json", "settings", "admin", "sign-in", "set-password"])
  const orgSlug = segments[0]
  if (reserved.has(orgSlug)) return
  // Detect URL shape from segment[1].
  const second = segments[1]
  // Event-reserved sub-paths under /<org>/ that AREN'T an event slug.
  const orgScopedReserved = new Set(["settings", "clients"])

  if (!second || orgScopedReserved.has(second)) {
    // Org-only scope. Resolve from the BA org-membership list first so
    // orgs with zero events still activate correctly on settings pages.
    await ensureUserOrgsLoaded()
    const state = useEvents.getState()
    const orgId = orgIdBySlugFromStores(orgSlug)
    if (!orgId) return
    if (state.activeOrgId === orgId) return
    if (inFlightActivate) return inFlightActivate
    inFlightActivate = (async () => {
      try { await state.activateOrg(orgId) } finally { inFlightActivate = null }
    })()
    return inFlightActivate
  }

  // Event scope: /<org>/<event>/...
  // Block until events list has loaded — event routes resolve
  // orgSlug/eventSlug → ids from the administrable event list.
  await ensureEventsLoaded()
  const state = useEvents.getState()

  const eventSlug = second
  const row = state.bySlug(orgSlug, eventSlug)
  if (!row?.eventId) return
  if (state.activeEventId === row.eventId) return
  if (inFlightActivate) return inFlightActivate
  inFlightActivate = (async () => {
    try { await state.activate(row.eventId!) } finally { inFlightActivate = null }
  })()
  return inFlightActivate
}

/// Resolve a URL slug pair → the real eventId, awaiting the admin-events list
/// if it hasn't loaded yet. Non-hook; safe to call from a router loader (where
/// hooks/stores-as-hooks aren't available). Returns undefined when the slugs
/// don't match any administrable event (e.g. bad URL, or the user lost access).
export async function resolveEventId(
  orgSlug: string,
  eventSlug: string,
): Promise<string | undefined> {
  await ensureEventsLoaded()
  return useEvents.getState().bySlug(orgSlug, eventSlug)?.eventId
}

/// Resolve a URL orgSlug → orgId. Awaits both the BA org-membership list and
/// the admin-events list (org may have zero events, or live only in the events
/// list). Non-hook; safe to call from a router loader.
export async function resolveOrgId(orgSlug: string): Promise<string | undefined> {
  await Promise.all([ensureUserOrgsLoaded().catch(() => {}), ensureEventsLoaded()])
  return orgIdBySlugFromStores(orgSlug)
}

/// Convenience hook used by every page rendered under `$orgSlug/$eventSlug`.
/// Reads the URL slugs, resolves to a MyEventRow, fires the BA set-active
/// sync when the event changes, and returns the resolved ids so the page
/// can render with the numeric eventId it actually wants.
///
/// Returns undefined fields when the slugs don't match (events list still
/// loading, or invalid URL). Pages should gate their content on `eventId`
/// being defined.
export function useActiveEvent(): {
  orgSlug: string | undefined
  eventSlug: string | undefined
  eventId: string | undefined
  organizationId: string | undefined
  row: MyEventRow | undefined
} {
  const params = useParams({ strict: false }) as {
    orgSlug?: string
    eventSlug?: string
  }
  const { orgSlug, eventSlug } = params
  const row = useEvents((s) =>
    orgSlug && eventSlug ? s.bySlug(orgSlug, eventSlug) : undefined,
  )
  const activate = useEvents((s) => s.activate)

  useEffect(() => {
    if (!row?.eventId) return
    void activate(row.eventId).catch((err) => {
      console.warn("[active-event] set-active failed", err)
    })
  }, [row?.eventId, activate])

  return {
    orgSlug,
    eventSlug,
    eventId: row?.eventId,
    organizationId: row?.organizationId ?? undefined,
    row,
  }
}

/// Hook for /<org>/settings/* routes. Resolves the URL orgSlug → orgId from
/// the BA org-membership list (falling back to event rows) and fires
/// activateOrg() when the BA session is pinned to a different org.
/// Org-only — there's no team concept (BA teams retired), so this never
/// touches event activation.
export function useActiveOrg(): {
  orgSlug: string | undefined
  organizationId: string | undefined
} {
  const params = useParams({ strict: false }) as { orgSlug?: string }
  const { orgSlug } = params
  const orgsLoaded = useUserOrgs((s) => s.loaded)
  const orgsLoading = useUserOrgs((s) => s.loading)
  const loadOrgs = useUserOrgs((s) => s.load)
  const orgIdFromOrgs = useUserOrgs((s) =>
    orgSlug ? s.orgs.find((org) => org.slug === orgSlug)?.id : undefined,
  )
  const orgIdFromEvents = useEvents((s) =>
    orgSlug ? s.orgIdBySlug(orgSlug) : undefined,
  )
  const organizationId = orgIdFromOrgs ?? orgIdFromEvents
  const activateOrg = useEvents((s) => s.activateOrg)

  useEffect(() => {
    if (!orgsLoaded && !orgsLoading) void loadOrgs()
  }, [orgsLoaded, orgsLoading, loadOrgs])

  useEffect(() => {
    if (!organizationId) return
    void activateOrg(organizationId).catch((err) => {
      console.warn("[active-org] set-active failed", err)
    })
  }, [organizationId, activateOrg])

  return {
    orgSlug,
    organizationId,
  }
}
